/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or
 * distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly
 * prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this
 * content, please contact the author for proper authorization and agreement.
 */

import React from 'react'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import { Reservation } from 'types/entities/Reservation'
import { currencyNumberFormat } from 'utils/utility'

interface BookingSellerReservationDetailRevenueSummaryProps {
    reservation: Reservation
    packagesCartPrice?: number | null
    className?: string
}

const parseAmount = (value: string | number | null | undefined): number | null => {
    if (value === null || value === undefined || value === '') {
        return null
    }
    const parsed = parseFloat(String(value))
    return Number.isNaN(parsed) ? null : parsed
}

const formatAmount = (value: number | null): string =>
    value === null ? 'CHF 0.00' : currencyNumberFormat(value)

const BookingSellerReservationDetailRevenueSummary: React.FC<
    BookingSellerReservationDetailRevenueSummaryProps
> = ({ reservation, packagesCartPrice, className }) => {
    const { t } = useTranslation()

    const reserved = parseAmount(reservation.price)
    const effective = parseAmount(reservation.total_invoice)
    const rechnung = parseAmount(reservation.total_invoice_rechnung)
    const checkout = parseAmount(reservation.total_invoice_checkout)
    const cartPrice = parseAmount(packagesCartPrice ?? null)

    const difference =
        reserved !== null && effective !== null ? effective - reserved : null

    const heroAmount = effective ?? cartPrice
    const heroLabel = effective !== null ? t('Effektiver Umsatz') : t('Total price')

    return (
        <div
            className={clsx(
                'BookingSellerReservationDetailRevenueSummary UiCard p-6 flex flex-col gap-4',
                className
            )}
        >
            <h3 className="text-lg font-bold border-b border-gray-100 dark:border-gray-800 pb-2">
                {t('Summary')}
            </h3>

            <div className="flex justify-between items-center">
                <span className="text-xl font-medium text-gray-600 dark:text-gray-400">
                    {heroLabel}
                </span>
                <span className="text-3xl font-black text-blue-600 dark:text-blue-400">
                    {formatAmount(heroAmount)}
                </span>
            </div>

            <div className="flex flex-col gap-2 text-sm">
                {reserved !== null && (
                    <div className="flex justify-between items-center gap-2">
                        <span className="text-gray-600 dark:text-gray-400">
                            {t('Reservierter Umsatz')}
                        </span>
                        <span className="font-medium text-gray-900 dark:text-gray-100">
                            {formatAmount(reserved)}
                        </span>
                    </div>
                )}

                {rechnung !== null && (
                    <div className="flex justify-between items-center gap-2 pl-3">
                        <span className="text-gray-500 dark:text-gray-400">
                            {t('Rechnung')}
                        </span>
                        <span className="font-medium text-gray-900 dark:text-gray-100">
                            {formatAmount(rechnung)}
                        </span>
                    </div>
                )}

                {checkout !== null && (
                    <div className="flex justify-between items-center gap-2 pl-3">
                        <span className="text-gray-500 dark:text-gray-400">
                            {t('Checkout')}
                        </span>
                        <span className="font-medium text-gray-900 dark:text-gray-100">
                            {formatAmount(checkout)}
                        </span>
                    </div>
                )}

                {difference !== null && (
                    <div className="flex justify-between items-center gap-2 border-t border-gray-100 dark:border-gray-800 pt-2">
                        <span className="text-gray-600 dark:text-gray-400">
                            {t('Difference')}
                        </span>
                        <span
                            className={clsx(
                                'font-semibold',
                                difference > 0 && 'text-green-600 dark:text-green-400',
                                difference < 0 && 'text-red-600 dark:text-red-400',
                                difference === 0 && 'text-gray-900 dark:text-gray-100'
                            )}
                        >
                            {difference > 0 ? '+' : ''}
                            {formatAmount(difference)}
                        </span>
                    </div>
                )}

                {cartPrice !== null && effective !== null && (
                    <div className="flex justify-between items-center gap-2 border-t border-gray-100 dark:border-gray-800 pt-2">
                        <span className="text-gray-600 dark:text-gray-400">
                            {t('Total price')}
                        </span>
                        <span className="font-medium text-gray-900 dark:text-gray-100">
                            {formatAmount(cartPrice)}
                        </span>
                    </div>
                )}
            </div>
        </div>
    )
}

export default BookingSellerReservationDetailRevenueSummary
